import React, { createContext, useContext, useState, useEffect } from 'react';

export type Language = 'he' | 'en';

interface LocaleContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

// Translation data
const translations = {
  he: {
    // Language selector
    'language.selector.title': 'בחר שפה',
    'language.selector.hebrew': 'עברית',
    'language.selector.english': 'English',
    'language.selector.continue': 'המשך',
    
    // Assessment page
    'assessment.loading': 'טוען הערכה..',
    'assessment.error': 'נכשל בטעינת ההערכה. אנא נסה שנית מאוחר יותר',
    
    // Participant form
    'participant.form.title': 'שלב אחרון!',
    'participant.form.subtitle': 'אנא מלאו את הפרטים כדי לסיים ולקבל את התוצאות',
    'participant.form.name': 'שם מלא',
    'participant.form.email': 'כתובת אימייל',
    'participant.form.phone': 'מספר טלפון',
    'participant.form.organization': 'ארגון',
    'participant.form.position': 'תפקיד',
    'participant.form.position.owner': 'בעלים',
    'participant.form.position.coowner': 'שותף',
    'participant.form.position.ceo': 'מנכ"ל',
    'participant.form.position.manager': 'מנהל',
    'participant.form.address': 'כתובת',
    'participant.form.employeeCount': 'מספר עובדים',
    'participant.form.submit': 'שלח וקבל תוצאות',
    'participant.form.submitting': 'שולח..',
    
    // Validation messages
    'validation.required': 'שדה חובה',
    'validation.email': 'כתובת אימייל לא תקינה',
    
    // Single question page
    'question.progress': 'שאלה {current} מתוך {total}',
    'question.complete.percent': '{percent}% הושלם',
    'question.keyboard.help': 'השתמש במקלדת: 1 = כן בדרך-כלל כן, 2 = לא בטוח אולי, 3 = לא בדרך כלל לא, ← = חזור',
    'question.previous': 'קודם',
    'question.next': 'הבא',
    'question.complete': 'סיים',
    'question.saving': 'שומר..',
    'question.select.answer': 'בחר את תשובתך',
    'question.final.message': 'שאלה אחרונה - בחירה תסיים את ההערכה',
    'question.all.answered': 'ענית על כל {total} השאלות!',
    
    // Answer options
    'answer.positive': 'כן, בדרך-כלל כן',
    'answer.neutral': 'לא בטוח, אולי', 
    'answer.negative': 'לא. בדרך כלל לא',
    
    // Toast messages
    'toast.session.started': 'הערכה התחילה בהצלחה!',
    'toast.session.error': 'נכשל להתחיל הערכה. אנא נסה שנית',
    'toast.response.error': 'נכשל לשלוח תשובה. אנא נסה שנית',
    'toast.assessment.completed': 'הערכה הושלמה בהצלחה!',
    'toast.select.answer': 'אנא בחר תשובה',
    
    // Validation messages
    'validation.missing.answers': 'חסרות תשובות ל-{{count}} שאלות. אנא השב על כל השאלות לפני הסיום',
    'validation.incomplete.assessment': 'המבדק לא הושלם. אנא וודא שענית על כל השאלות',
    
    // Results page
    'results.title': 'תודה!',
    'results.subtitle': 'ההערכה הושלמה בהצלחה',
    'results.message': '',
    'results.contact': 'נהיה בקשר בקרוב',
    'results.chart.title': 'התוצאות שלך',
    'results.completed.on': 'הושלם ב',
    'results.new.assessment': 'הערכה חדשה',
    'results.learnMore': 'גלה עוד על פדרמן יועצים',

    // Homepage
      'homepage.title': 'יכול להיות שהכיוון בכלל לא נכון',
      'homepage.subtitle': 'עסק טוב זה לא עניין של מזל, זה עניין של דיוק',
    'homepage.description': 'עובדים קשה, מביאים תוצאות, ועדיין משהו תקוע? יכול להיות שזה רק אחוזים קטנים מהפוטנציאל האמיתי.',
      'homepage.description2': 'המבדק הזה יראה את מה שאף אחד לא יגיד בפנים: איפה מתבזבז זמן, איפה נשרף כסף ואיפה פשוט מפספסים.',
      'homepage.intro': 'מלאו עכשיו את מבדק ניתוח הפוטנציאל העסקי וגלו מה מכשיל אתכם',
      'homepage.analysis': 'זו לא תחושת בטן - זה ניתוח מדויק של הפער בין ניהול נוכחי לאן שבאמת אפשר להגיע.',
      'homepage.proof': 'מאות מנכ"לים ובעלי עסקים כבר עברו את זה. חלקם גילו שהם לא צריכים עוד עובדים - רק לשנות נקודת מבט. אחרים גילו שהבעיה היא הם.',
      'homepage.invitation': 'לא צריך לספר כלום. רק לענות בכנות על השאלות במבדק - והנתונים כבר יספרו את האמת.',
      'homepage.comment': 'המבדק מתאים לבעלי חברות עם 15 עובדים ומעלה.',

      'homepage.testimonial1.name': 'אמנון שטרן, שטרן גרופ:',
      'homepage.testimonial1.quote': 'המספרים והנתונים דיברו בעד עצמם. ראינו איפה הבעיות האמיתיות ואיפה יש לנו הזדמנויות צמיחה - וזה קרה ללא ספק.',
      'homepage.testimonial1.quote2': 'כיום אנחנו חברה שצמחה באלפי אחוזים ואחת מחברות הבניין המובילות בישראל.',

      'homepage.testimonial2.name': 'רונן חן, מעצב אופנה ובעלים של RONEN CHEN:',
      'homepage.testimonial2.quote': 'אבי ידע להציג את התמונה דרך קריאת הנתונים בצורה חדה. זה מה שהבהיר לנו מה חייב להשתנות ואיך להפוך את החלום למציאות.',
      'homepage.testimonial2.quote2': 'ההבנה שהשינוי מתחיל מבפנים היא מה שהניעה אותנו קדימה.',
      'homepage.testimonial2.quote3': 'כיום אני מעצב האופנה הישראלי עם רשת החנויות הגדולה ביותר.',
      'homepage.benefit1': 'לוקח מספר דקות.',
      'homepage.benefit2': 'משנה חשיבה.',
      'homepage.benefit3': 'מביא לעסק תוצאות כספיות מוכחות.',
      'homepage.cta': 'ההתחלה לשינוי מתחילה כאן - לחצו והתחילו!',
      'homepage.button': 'כן, אני רוצה למלא את המבדק!',

    // General
    'general.loading': 'טוען..',
    'general.error': 'שגיאה',
    'general.continue': 'המשך',
    'general.back': 'חזור',
    'general.submit': 'שלח',
    'general.cancel': 'ביטול',
  },
  en: {
    // Language selector
    'language.selector.title': 'Select Language',
    'language.selector.hebrew': 'עברית',
    'language.selector.english': 'English',
    'language.selector.continue': 'Continue',
    
    // Assessment page
    'assessment.loading': 'Loading assessment..',
    'assessment.error': 'Failed to load assessment. Please try again later',
    
    // Participant form
    'participant.form.title': 'Final Step!',
    'participant.form.subtitle': 'Please provide your information to complete and receive your results',
    'participant.form.name': 'Full Name',
    'participant.form.email': 'Email Address',
    'participant.form.phone': 'Phone Number',
    'participant.form.organization': 'Organization',
    'participant.form.position': 'Position',
    'participant.form.position.owner': 'Owner',
    'participant.form.position.coowner': 'Co-owner',
    'participant.form.position.ceo': 'CEO',
    'participant.form.position.manager': 'Manager',
    'participant.form.address': 'Address',
    'participant.form.employeeCount': 'Number of Employees',
    'participant.form.submit': 'Submit & Get Results',
    'participant.form.submitting': 'Submitting..',
    
    // Validation messages
    'validation.required': 'This field is required',
    'validation.email': 'Invalid email address',
    
    // Single question page
    'question.progress': 'Question {current} of {total}',
    'question.complete.percent': '{percent}% Complete',
    'question.keyboard.help': 'Use keyboard: 1 = I agree yes/mostly yes, 2 = Maybe uncertain, 3 = I disagree no/mostly no, ← = Back',
    'question.previous': 'Previous',
    'question.next': 'Next',
    'question.complete': 'Complete',
    'question.saving': 'Saving..',
    'question.select.answer': 'Select your answer',
    'question.final.message': 'Final question - selection will complete assessment',
    'question.all.answered': 'You answered all {total} questions!',
    
    // Answer options
    'answer.positive': 'I agree, yes/ mostly yes',
    'answer.neutral': 'Maybe, uncertain, neither yes nor no',
    'answer.negative': 'I disagree, no or mostly no',
    
    // Toast messages
    'toast.session.started': 'Assessment session started successfully!',
    'toast.session.error': 'Failed to start assessment session. Please try again',
    'toast.response.error': 'Failed to submit response. Please try again',
    'toast.assessment.completed': 'Assessment completed successfully!',
    'toast.select.answer': 'Please select an answer',
    
    // Validation messages
    'validation.missing.answers': 'Missing answers for {{count}} questions. Please answer all questions before completing',
    'validation.incomplete.assessment': 'Assessment is incomplete. Please ensure you have answered all questions',
    
    // Results page
    'results.title': 'Thank You!',
    'results.subtitle': 'Assessment completed successfully',
    'results.message': '',
    'results.contact': 'We will be in touch soon',
    'results.chart.title': 'Your Results',
    'results.completed.on': 'Completed on',
    'results.new.assessment': 'New Assessment',
    'results.learnMore': 'Discover Federman Consultants',
    
    
    // Homepage
    'homepage.title': 'You might be on the wrong track',
    'homepage.subtitle': 'A good business isn\'t about luck - it\'s about precision',
    'homepage.description': 'If you feel like you\'re working hard, getting results, but something\'s still stuck - you might be realizing only a small percentage of your potential',
    'homepage.description2': 'This assessment will show you what no one will tell you to your face: where you\'re wasting time, where you\'re burning money, and where you\'re just missing out.',
  'homepage.intro': 'Take now business analysis test and find out what is failing you',
    'homepage.analysis': 'This isn\'t gut feeling. It\'s a precise analysis of the gap between how you manage and where you can really reach',
    'homepage.proof': 'Hundreds of CEOs and business owners have already done this. Some discovered they don\'t need more employees - just a change in perspective. Others discovered the problem is them',
    'homepage.invitation': 'You don\'t need to tell me anything. Just answer the assessment questions honestly and the data will tell the truth',
    'homepage.comment': 'The assessment is suitable for company owners with 15+ employees',

    'homepage.testimonial1.name': 'Amnon Stern, Owner of Stern Group:',
    'homepage.testimonial1.quote': '"The numbers and data spoke for themselves. We saw where the real problems were and where we had growth opportunities – and this happened without a doubt"',
    'homepage.testimonial1.quote2': '"Today we are a company that has grown by thousands of percent and one of the leading construction companies in Israel"',

    'homepage.testimonial2.name': 'Ronen Chen, Fashion Designer and Owner of RONEN CHEN Fashion House:',
    'homepage.testimonial2.quote': '"Avi knew how to present the picture through sharp data reading. This is what clarified to us what must change and how to turn the dream into reality"',
    'homepage.testimonial2.quote2': '"Today we are a leading fashion house in Israel, thanks to the insights we gained from the assessment"',
    'homepage.testimonial2.quote3': '"Today I am the Israeli fashion designer with the largest chain of stores"',
    
    'homepage.benefit1': 'Takes a few minutes',
    'homepage.benefit2': 'Changes mindset',
    'homepage.benefit3': 'Brings proven financial results to the business',
    'homepage.cta': 'The beginning of change starts here, click and start!',
    'homepage.button': 'Yes, I want to take the assessment!',
    
    // General
    'general.loading': 'Loading..',
    'general.error': 'Error',
    'general.continue': 'Continue',
    'general.back': 'Back',
    'general.submit': 'Submit',
    'general.cancel': 'Cancel',
  }
};

export const LocaleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>(() => {
    // Try to get saved language from localStorage, default to Hebrew
    const saved = localStorage.getItem('assessment-language');
    return (saved as Language) || 'he';
  });

  useEffect(() => {
    // Save language preference to localStorage
    localStorage.setItem('assessment-language', language);
    
    // Update document direction for RTL/LTR
    document.documentElement.dir = language === 'he' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language]);

  const t = (key: string, params?: Record<string, string | number>): string => {
    const translationObject = translations[language] as Record<string, string>;
    let text = translationObject?.[key] || key;
    
    // Replace parameters in the text
    if (params) {
      Object.keys(params).forEach(param => {
        text = text.replace(`{${param}}`, String(params[param]));
      });
    }
    
    return text;
  };

  return (
    <LocaleContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LocaleContext.Provider>
  );
};

export const useLocale = () => {
  const context = useContext(LocaleContext);
  if (context === undefined) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context;
};